import { Stack } from '@mui/material';
import { FieldValues } from 'react-hook-form';

import {
  FormAwareElementProps,
  FormAwareInputProps,
} from '@/components/form/formAwareInputs/FormAwareInput.types';
import { AISuggestedValue } from '@/modules/aiSuggestions/AISuggestedValue/AISuggestedValue';
import { SuggestableKind } from '@/modules/aiSuggestions/aiSuggestions.types';
import {
  FormAwareLocateSuggestionButton,
  FormAwareLocateSuggestionButtonVariant,
} from '@/modules/aiSuggestions/components/forms/FormAwareLocateSuggestionButton';
import { useAISuggestionsEnabled } from '@/modules/aiSuggestions/hooks/useAISuggestionsEnabled';
import { useSuggestableInputHandlers } from '@/modules/aiSuggestions/hooks/useSuggestableInputHandlers';
import { useEditEntitySplitScreenModalContext } from '@/modules/entities/EditEntitySplitScreen/contexts/editEntitySplitScreenModal.context';

import { useEntitySuggestionsContext } from '../../context/EntitySuggestions.context';

interface SuggestableFormAwareInputProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> {
  suggestionKind: SuggestableKind;
  FormAwareElement: FormAwareElementProps<S, T>;
}

type SuggestableFormAwareInputCombinedProps<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
> = SuggestableFormAwareInputProps<S, T> & T;

/**
 * A wrapper around a FormAwareInput that displays a suggested value for the
 * field, and allows the user to accept or reject the suggestion.
 *
 * If accepted, the suggestion will be set as the field's value, and the form
 * will be marked as dirty.
 *
 * NOTE: This only supports suggestions for fields that are single-valued, like
 * text inputs, single select inputs, date pickers, etc.
 */
export function SuggestableFormAwareInput<
  S extends FieldValues,
  T extends FormAwareInputProps<S>,
>(props: SuggestableFormAwareInputCombinedProps<S, T>) {
  const { entityType } = useEditEntitySplitScreenModalContext();
  const aiSuggestionsEnabled = useAISuggestionsEnabled(entityType);

  const { fieldName, suggestionKind, FormAwareElement } = props;
  const { getSuggestableSuggestion } = useEntitySuggestionsContext();

  const {
    suggestion,
    isAcknowledged,
    handleAcceptSuggestion,
    handleRejectSuggestion,
  } = useSuggestableInputHandlers({
    fieldName,
    suggestionKind,
    getSuggestableSuggestion,
  });

  if (!aiSuggestionsEnabled) {
    // Return the FormAwareElement as is, if AI suggestions are disabled
    return <FormAwareElement {...props} />;
  }

  return (
    <Stack>
      <FormAwareElement
        {...props}
        labelIconEnd={
          <FormAwareLocateSuggestionButton
            suggestion={suggestion}
            variant={FormAwareLocateSuggestionButtonVariant.MATCHER}
          />
        }
      />
      {suggestion && !isAcknowledged && (
        <AISuggestedValue
          suggestion={suggestion}
          onAcceptSuggestion={handleAcceptSuggestion}
          onRejectSuggestion={handleRejectSuggestion}
        />
      )}
    </Stack>
  );
}
